import { h, tag } from "omi";
import some from "lodash-es/some";

import { NWAccordion, NwAccordionItem } from "@/wc/components/atoms/nw-accordion";
import { PureComponent } from "@/utils/pureComponent.tsx";
import { Day, Project, ProjectStatus, Schedule, Task } from "@/api";
import { NWError } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { useSchedule } from "../store/queries/schedule.ts";
import { NavScheduleSkeleton } from "./navScheduleSkeleton.tsx";
import { NWProjectStatusIndicator } from "./projectStatusIndicator.tsx";

type Props = {};

@tag("nav-schedule")
export class NavSchedule extends PureComponent<Props> {
  static css = [tailwind];

  render() {
    const { data, isLoading, error } = useSchedule();
    const schedule = data.value;

    if (isLoading.value) {
      return (
        <div class="animate-fadeInFast">
          <NavScheduleSkeleton />
        </div>
      );
    }

    if (error.value != null) {
      return (
        <div class="animate-fadeInFast">
          <NWError />
        </div>
      );
    }

    return (
      <div class="animate-fadeInFast">
        {schedule?.weeks.map(({ id, days, status }) => (
          <div key={id} class="border-b border-gray-200 mb-6 pb-6 last:mb-0 last:pb-0 last:border-0">
            <NWAccordion
              openByDefault={isWeekActive(days) || schedule?.weeks.length === 1 || hasOnlyOneTask(schedule)}
              useSlotForLabel
            >
              <div slot="label" class="flex items-center space-x-3">
                <NWProjectStatusIndicator isActive={isWeekActive(days)} status={status as ProjectStatus} />
                <p>Week {id}</p>
              </div>

              {days.map(({ id, tasks, status }) => (
                <NWAccordion openByDefault={isDayActive(tasks) || hasOnlyOneTask(schedule)} nested useSlotForLabel>
                  <div slot="label" class="flex items-center space-x-3">
                    <NWProjectStatusIndicator isActive={isDayActive(tasks)} status={status as ProjectStatus} />
                    <p>Day {id}</p>
                  </div>

                  {tasks.map(({ id, name, status }) => (
                    <NwAccordionItem
                      type="link"
                      label={name}
                      isActive={isActiveProject(id)}
                      withActiveIndicator={false}
                      href={`/projects/${id}`}
                      nested
                    >
                      <NWProjectStatusIndicator isActive={isActiveProject(id)} status={status as ProjectStatus} />
                    </NwAccordionItem>
                  ))}
                </NWAccordion>
              ))}
            </NWAccordion>
          </div>
        ))}
      </div>
    );
  }
}

function isDayActive(tasks: Task[]) {
  return some(tasks, ({ id }) => isActiveProject(id));
}

function isWeekActive(days: Day[]) {
  return some(days, ({ tasks }) => isDayActive(tasks));
}

function isActiveProject(projectId: Project["id"]) {
  return projectId ? projectId === Store.project.id.value : false;
}

function hasOnlyOneTask(schedule: Schedule) {
  return (
    schedule.weeks.length === 1 && schedule.weeks[0].days.length === 1 && schedule.weeks[0].days[0].tasks.length === 1
  );
}
